import {
  Download,
  OpenInNew,
  ContentCopy,
  Check,
  Close,
} from "@mui/icons-material";
import {
  Button,
  Checkbox,
  CircularProgress,
  Slider,
  Snackbar,
  Tooltip,
} from "@mui/material";
import { useMemo, useState } from "react";
import styled, { createGlobalStyle } from "styled-components/macro";
import {
  AUTO_DOWNLOAD_IMG,
  useIsTakingSnapshot,
} from "./SnapshotElementButton";
import { startCase } from "lodash";
import { ONE_DAY_OF_MS, ONE_MINUTE_OF_MS } from "utils/timeUtils";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useProcessesInVisibleGroups } from "utils/customHooks/genericHooks";
import { SnapshotsProgressStatus } from "./SnapshotsProgressStatus";
import { useLocation } from "react-router";
import { useEffect } from "react";
import { useAtom } from "jotai";
import { allTimelinesLoadedAtom } from "components/Dashboard/Timeline/ConnectedTimeline";

export function SnapshotsCSVUploader() {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snapshotsInterval, setSnapshotsInterval] = useState(10 * 1000);
  const [whichComponents, setWhichComponents] = useState([
    AUTO_DOWNLOAD_IMG.pnl,
  ]);
  // how many snapshots are complete
  const [snapshotsProgress, setSnapshotsProgress] = useState({
    current: 0,
    total: 0,
  });

  const ckey = useCustomerKey();
  const allProcesses = useProcessesInVisibleGroups();
  const workScheduleId = allProcesses[0]?.workScheduleConfig.id;

  /** open a new window with url params according to which processes, range, and components you want images of */
  function takeSnapshot({
    processNames,
    startTime,
    endTime,
  }: {
    processNames: string;
    startTime: string;
    endTime: string;
  }) {
    const processesIds = processNames.split(";").map(name => {
      const process = allProcesses.find(p => p.name === name);
      return process?.id ?? "";
    });
    if (processesIds.length === 0) {
      setError("No processes found for the given names");
      return;
    }

    const url = `/${ckey}/dashboard/fromSnapshots?view=tl&workSchedule=${workScheduleId}&processes=${processesIds.join(
      ","
    )}&ge=${startTime}&lt=${endTime}&hideTrend=true&hideSummary=true${
      whichComponents.includes(AUTO_DOWNLOAD_IMG.pnl)
        ? `&autoDownload=${AUTO_DOWNLOAD_IMG.pnl}`
        : ""
    }${
      whichComponents.includes(AUTO_DOWNLOAD_IMG.timeline)
        ? `&autoDownload=${AUTO_DOWNLOAD_IMG.timeline}`
        : ""
    }&hideMetricsColumn=false`;

    window.open(url, "_blank", "noopener,noreferrer");
  }

  /** call takeSnapshot function in sequence using specified interval */
  async function handleClickTakeSnapshots() {
    if (!file) {
      return;
    }
    const { csvRows, rows }: { csvRows: string[]; rows: any[] } =
      await parseUploadedCsv(file);
    setSnapshotsProgress({
      current: 0,
      total: csvRows.length - 1,
    });

    const allSnapshots: {
      processNames: string;
      startTime: string;
      endTime: string;
    }[] = rows.map(row => {
      const {
        "Process Names": processNames,
        "Start Time": startTime,
        "End Time": endTime,
      } = row;
      return {
        processNames,
        startTime,
        endTime,
      };
    });

    // - create an array of promises, and use Promise.allSettled to detect when they're all complete
    const allPromises = allSnapshots.map(
      (snapshot, idx) =>
        new Promise(resolve => {
          setTimeout(() => {
            takeSnapshot(snapshot);
            // - after each promise completes, increment snapshotsProgress
            setSnapshotsProgress(prev => ({
              ...prev,
              current: prev.current + 1,
            }));
            resolve(true);
          }, snapshotsInterval * idx);
        })
    );

    await Promise.allSettled(allPromises);

    return;
  }

  const isTakingSnapshots =
    snapshotsProgress.total !== snapshotsProgress.current;
  const disabled = !file || isTakingSnapshots || whichComponents.length === 0;

  const [{ maxDaysInOneRow, maxProcessesInOneRow }, setSnapshotsMetadata] =
    useState({
      maxDaysInOneRow: 0,
      maxProcessesInOneRow: 0,
    });
  // when the csv is uploaded, set min snapshots interval according to how many processes per row & max time range
  useEffect(() => {
    (async () => {
      if (!file) {
        return;
      }
      const { rows }: { csvRows: string[]; rows: any[] } =
        await parseUploadedCsv(file);
      const processes = rows.map(row => row["Process Names"]);
      const maxProcesses = Math.max(...processes.map(p => p.split(";").length));

      const maxTimeRangeInOneRow = Math.max(
        ...rows.map(row => {
          const start = new Date(Number(row["Start Time"])).getTime();
          const end = new Date(Number(row["End Time"])).getTime();
          return end - start;
        })
      );
      const maxDays = Math.ceil(maxTimeRangeInOneRow / ONE_DAY_OF_MS);

      setSnapshotsMetadata({
        maxDaysInOneRow: maxDays,
        maxProcessesInOneRow: maxProcesses,
      });
    })();
  }, [file]);

  // e.g. for 5 processes, set min interval to 5 seconds
  let minSnapshotsInterval = Math.max(
    5 * 1000,
    1 * 1000 * maxProcessesInOneRow
  );
  if (maxDaysInOneRow > 1) {
    // e.g. if 30 days, interval = 30 seconds
    minSnapshotsInterval = Math.max(
      minSnapshotsInterval,
      maxDaysInOneRow * 1000
    );
  }

  return (
    <Styles>
      <br />
      <p>
        <strong>1. Upload a CSV</strong>
      </p>
      <br />
      <div className="upload-csv">
        <input
          type="file"
          accept=".csv"
          onChange={e => {
            const file = e.target.files?.item(0);
            if (file) {
              setFile(file);
            }
          }}
        />
      </div>
      {file ? (
        <>
          <PreviewCsvFile {...{ file }} />
          <div style={{ marginLeft: 32, marginTop: 12, fontSize: 12 }}>
            <div>max processes per row: {maxProcessesInOneRow}</div>
            <div>max range per row: {maxDaysInOneRow} days</div>
            <div>
              {"=>"} min snapshot interval = {minSnapshotsInterval / 1000}{" "}
              seconds
            </div>
          </div>
        </>
      ) : null}
      <br />
      <br />
      <p>
        <strong>2. Which components do you want to snapshot?</strong>
      </p>
      <br />
      {/* checkboxes for each component */}
      <div className="checkboxes">
        {Object.keys(AUTO_DOWNLOAD_IMG).map(component => (
          <div key={component}>
            <Checkbox
              size="small"
              checked={whichComponents.includes(component)}
              onChange={() => {
                setWhichComponents(
                  whichComponents.includes(component)
                    ? whichComponents.filter(c => c !== component)
                    : [...whichComponents, component]
                );
              }}
              inputProps={{ "aria-label": component }}
            />
            <label>{startCase(component)}</label>
          </div>
        ))}
      </div>
      <br />
      <p>
        <strong>3. Set the interval for taking snapshots:</strong>
      </p>
      <br />
      <small style={{ fontSize: 12 }}>
        I.e. time between processing each csv-row. For larger requests (longer
        time range or more processes), set this higher to avoid crashing the
        server.
      </small>
      <br />
      <Slider
        value={snapshotsInterval}
        onChange={(_, value) => setSnapshotsInterval(value as number)}
        step={5 * 1000}
        min={minSnapshotsInterval}
        defaultValue={10 * 1000}
        max={1 * 60 * 1000} // 1 minute
        valueLabelDisplay="auto"
        valueLabelFormat={value => `${value / 1000} seconds`}
        aria-label="Snapshots interval"
        style={{ marginTop: 6, marginBottom: -6 }}
      />
      <br />
      <br />
      <Button
        fullWidth={true}
        onClick={handleClickTakeSnapshots}
        variant="contained"
        style={{ color: "white" }}
        endIcon={
          isTakingSnapshots ? <CircularProgress size={24} /> : <OpenInNew />
        }
        disabled={disabled}
      >
        {isTakingSnapshots ? "Taking Snapshots..." : "Take Snapshots"}
        {error && <span style={{ color: "red" }}>{error}</span>}
      </Button>
      <br />
      <br />
      {snapshotsProgress.total !== 0 ? (
        <>
          <SnapshotsProgressStatus {...{ snapshotsProgress }} />
          <br />
          <br />
        </>
      ) : null}
      <CSVInstructions />
    </Styles>
  );
}

async function parseUploadedCsv(file: File) {
  const csv = await file.text();
  const csvRows = csv.split("\n");
  const headerRow = csvRows.shift();
  const header = headerRow?.split(",");
  const rows: any[] = csvRows.map(row => {
    const rowArray = row.split(",");
    return header?.reduce((acc, header, i) => {
      acc[header] = rowArray[i];
      return acc;
    }, {});
  });
  return { csvRows, rows };
}
const MAX_WAIT_FOR_TIMELINES_MS = 1.75 * ONE_MINUTE_OF_MS;
/** snackbar indicating "taking a snapshot... + circularProgress icon"" that disappears after 10 seconds */
export function TakingSnapshotToastIndicator() {
  const { pathname } = useLocation();
  const [wasTakingSnapshot, setWasTakingSnapshot] = useState(false);
  const [isTakingSnapshot] = useIsTakingSnapshot();
  const open =
    pathname.includes("fromSnapshots") || isTakingSnapshot || wasTakingSnapshot;
  return open ? (
    <TakingSnapshotToastIndicatorContent
      {...{ setWasTakingSnapshot, wasTakingSnapshot }}
    />
  ) : null;
}
export function TakingSnapshotToastIndicatorContent({
  setWasTakingSnapshot,
  wasTakingSnapshot,
}: {
  setWasTakingSnapshot: React.Dispatch<React.SetStateAction<boolean>>;
  wasTakingSnapshot: boolean;
}) {
  const [isTakingSnapshot] = useIsTakingSnapshot();

  const [timedOut, setTimedOut] = useState(false);
  // reset timedOut
  useEffect(() => {
    if (isTakingSnapshot) {
      setTimedOut(true);
    }
  }, [isTakingSnapshot]);
  // set wasTakingSnapshot to false after 5 seconds (manual implementation of Snackbar's autoHideDuration)
  useEffect(() => {
    if (isTakingSnapshot) {
      setWasTakingSnapshot(true);
    } else {
      setTimeout(() => {
        setWasTakingSnapshot(false);
      }, 5 * 1000);
    }
  }, [isTakingSnapshot, wasTakingSnapshot, setWasTakingSnapshot]);
  const [allTimelinesLoaded] = useAtom(allTimelinesLoadedAtom);
  const waitingForData = useMemo(() => {
    const allTimelinesLoadedValues = Object.values(allTimelinesLoaded);
    return (
      !timedOut &&
      (allTimelinesLoadedValues.length === 0 ||
        allTimelinesLoadedValues.some(isLoaded => !isLoaded))
    );
  }, [allTimelinesLoaded, timedOut]);

  // set timedOut to take the snapshot anyway if we waited for too long
  useEffect(() => {
    let timer;
    if (waitingForData) {
      timer = setTimeout(() => {
        setTimedOut(true);
      }, MAX_WAIT_FOR_TIMELINES_MS);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [timedOut, waitingForData]);
  return (
    <>
      {timedOut ? <SnapshotTimedOutGlobalStyles /> : null}
      <Snackbar
        open={true}
        // autoHideDuration={10 * 1000}
        message={
          <div
            style={{
              display: "flex",
              gap: "0.5em",
              alignItems: "center",
            }}
          >
            {isTakingSnapshot ? (
              <>
                <CircularProgress size={24} />
                <span style={{ marginLeft: 8 }}>
                  Taking a snapshot{" "}
                  {waitingForData
                    ? " (waiting for data)"
                    : " (processing HTML)"}
                  ...
                </span>
              </>
            ) : (
              // done taking snapshot, close button
              <>
                <Check />
                <div style={{ flexGrow: 1 }}>Snapshot taken!</div>
                <Button
                  style={{
                    marginLeft: "2em",
                    alignItems: "center",
                    textTransform: "none",
                  }}
                  variant={"outlined"}
                  size="small"
                  onClick={() => {
                    // close the current tab
                    window.close();
                  }}
                  endIcon={<Close />}
                >
                  Close the window
                </Button>
              </>
            )}
          </div>
        }
      />
    </>
  );
}

const SnapshotTimedOutGlobalStyles = createGlobalStyle`
  .production-segment-loading{
    display:none;
  }
`;
const Styles = styled.div`
  width: 100%;
  .checkboxes {
    display: flex;
    gap: 1em;
  }
  .upload-csv {
    display: flex;
    gap: 1em;
  }
`;
function PreviewCsvFile({ file }: { file: File }) {
  const [fileContent, setFileContent] = useState("");

  const reader = new FileReader();
  reader.onload = () => {
    setFileContent(String(reader.result));
  };

  reader.readAsText(file);
  return (
    <div style={{ margin: "0 2em", overflow: "auto" }}>
      <br />
      <p>File preview:</p>
      <br />
      <pre style={{ width: "fit-content" }}>{fileContent}</pre>
    </div>
  );
}
function CSVInstructions() {
  const [copied, setCopied] = useState(false);
  const allProcesses = useProcessesInVisibleGroups();
  const midnightToday = new Date().setHours(0, 0, 0, 0);
  const ckey = useCustomerKey();

  const example1ProcessNamesAndTimestamps = `Process Names,Start Time,End Time
${allProcesses?.[0]?.name};${allProcesses?.[1]?.name},${
    midnightToday - 3 * ONE_DAY_OF_MS
  },${midnightToday - 2 * ONE_DAY_OF_MS}
${allProcesses?.[2]?.name};${allProcesses?.[3]?.name},${
    midnightToday - 2 * ONE_DAY_OF_MS
  },${midnightToday - 1 * ONE_DAY_OF_MS}
${allProcesses?.[4]?.name};${allProcesses?.[5]?.name},${
    midnightToday - 1 * ONE_DAY_OF_MS
  },${midnightToday - 0 * ONE_DAY_OF_MS}`;

  const csvData1 = example1ProcessNamesAndTimestamps;
  const csvFileName1 = `snapshots_${ckey}_processnames_timestamps.csv`;
  const csvFile1 = new Blob([csvData1], { type: "text/csv;charset=utf-8;" });
  const exampleCsvUrl1 = URL.createObjectURL(csvFile1);

  function readableDate(date: Date) {
    return (
      date.toISOString().split("T")[0] +
      " " +
      date.toTimeString().split(" ")[0].split(":").slice(0, 2).join(":")
    );
  }

  const example2MachemetryIdsAndReadableDates = `Process Names,Start Time,End Time
${allProcesses?.[0]?.id};${allProcesses?.[1]?.id},${readableDate(
    new Date(midnightToday - 3 * ONE_DAY_OF_MS)
  )},${readableDate(new Date(midnightToday - 2 * ONE_DAY_OF_MS))}
${allProcesses?.[2]?.id};${allProcesses?.[3]?.id},${readableDate(
    new Date(midnightToday - 2 * ONE_DAY_OF_MS)
  )},${readableDate(new Date(midnightToday - 1 * ONE_DAY_OF_MS))}
${allProcesses?.[4]?.id};${allProcesses?.[5]?.id},${readableDate(
    new Date(midnightToday - 1 * ONE_DAY_OF_MS)
  )},${readableDate(new Date(midnightToday - 0 * ONE_DAY_OF_MS))}`;

  const csvData2 = example1ProcessNamesAndTimestamps;
  const csvFileName2 = `snapshots_${ckey}_processnames_timestamps.csv`;
  const csvFile2 = new Blob([csvData2], { type: "text/csv;charset=utf-8;" });
  const exampleCsvUrl2 = URL.createObjectURL(csvFile2);

  return (
    <InstructionsStyles>
      <p>
        <strong>{`CSV should have the following columns: "Process Names", "Start Time",
        "End Time"`}</strong>
      </p>
      <br />
      <small>
        <p>
          Process Names: process names or machemetry ids in a
          semicolon-separated list.
        </p>
      </small>
      <small>
        <p style={{ marginTop: 6 }}>Times: readable dates or timestamps.</p>
      </small>
      <br />
      <small className="csv-instructions">
        <p className="example-text">Example (process names + timestamps):</p>
        <div className="relative">
          <Tooltip title={copied ? "Copied!" : "Copy"} arrow placement="top">
            <ContentCopy
              className="copyIcon"
              onClick={() => {
                navigator.clipboard.writeText(exampleCsvUrl1);
                setCopied(true);
                setTimeout(() => {
                  setCopied(false);
                }, 1000);
              }}
              fontSize="small"
            />
          </Tooltip>
          <a
            className="download-this-csv"
            href={exampleCsvUrl1}
            download={csvFileName1}
          >
            Download this CSV
            <Download />
          </a>
        </div>
        <pre>
          <code>{example1ProcessNamesAndTimestamps}</code>
        </pre>
        <br />
        <p className="example-text">
          Example (machemetry ids + readable dates):
        </p>
        <div className="relative">
          <Tooltip title={copied ? "Copied!" : "Copy"} arrow placement="top">
            <ContentCopy
              className="copyIcon"
              onClick={() => {
                navigator.clipboard.writeText(exampleCsvUrl2);
                setCopied(true);
                setTimeout(() => {
                  setCopied(false);
                }, 1000);
              }}
              fontSize="small"
            />
          </Tooltip>
          <a
            className="download-this-csv"
            href={exampleCsvUrl2}
            download={csvFileName2}
          >
            Download this CSV
            <Download />
          </a>
        </div>
        <pre>
          <code>{example2MachemetryIdsAndReadableDates}</code>
        </pre>
      </small>
    </InstructionsStyles>
  );
}
const InstructionsStyles = styled.div`
  em {
    font-style: italic;
  }
  code {
    white-space: normal;
    word-wrap: break-word;
  }
  .download-this-csv {
    position: absolute;
    top: -18px;
    right: 0px;
    display: flex;
    align-items: center;
  }
  pre {
    margin-top: 0.5em;
  }
  .relative {
    cursor: pointer;
    position: relative;
    .MuiSvgIcon-root.copyIcon {
      position: absolute;
      top: 14px;
      right: 6px;
      transform: scale(0.75);
      transform-origin: top right;
    }
  }
  small {
    font-size: 12px;
  }
  .example-text {
    max-width: calc(100% - 30ch);
  }
`;
